import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useIsThreeDotsMenuVisible } from './useIsThreeDotsMenuVisible';
import { useInboundHearingCancelDelay } from './useInboundHearingCancelDelay';
import { useReconnectCleanupPending } from './useReconnectCleanupPending';

import { Hotkey } from 'config/enums';
import { useAppSelector } from 'features/app/hooks';
import {
  useCallDeafParticipantHandlers,
  useCallHearingParticipantHandlers,
} from 'features/call/call-accepted/hooks';
import { callSelector } from 'features/call/call-base/store/selectors';
import { deafSelector } from 'features/call/call-deaf/store';
import {
  CallDetailsButtonCalleeConnect,
  CallDetailsButtonCancel,
  CallDetailsButtonDeafReconnect,
  CallDetailsButtonDisconnect,
  CallDetailsButtonHearingDisconnect,
  CallDetailsButtonReconnect,
  CallDetailsParticipantIcon,
  CallDetailsDeafConnectedButtons,
  CallDetailsExtensionInputContainer,
} from 'features/call/call-details/components';
import type { CallDetailsParticipantInfo } from 'features/call/call-details/interfaces';
import { isHearingToDeafCallerLoadingSelector } from 'features/call/call-hearing-to-deaf/store';
import {
  activePrimaryHearingSelector,
  isPrimaryHearingReconnectSelector,
  hearingWSConnectionSelector,
} from 'features/call/call-hearing/store';
import { isEmergencyCallSelector } from 'features/call/vrs-call/store/vrsCallSelectors';
import { EmergencyCallDetailsCalleeConnecting } from 'features/emergency/components';
import { useSignMailHandlers } from 'features/signmail/hooks';
import {
  isAcceptingUserSelector,
  isTeamingInProgressSelector,
} from 'features/teaming/teaming-base/store';
import { CallDetailsParticipantVco2LineContainer } from 'features/vco-2-line/components';
import { useHearingCancel } from 'features/voice-meeting/hooks';
import { voiceSessionStatusSelector } from 'features/voice-session/store';

export const useCallDetailsCaller = () => {
  const { isDeafToHearing } = useAppSelector(callSelector);
  const deaf = useAppSelector(deafSelector);
  const primaryHearing = useAppSelector(activePrimaryHearingSelector);
  const isHearingToDeafCallerLoading = useAppSelector(
    isHearingToDeafCallerLoadingSelector
  );
  const isTeamingInProgress = useAppSelector(isTeamingInProgressSelector);
  const isAcceptingUser = useAppSelector(isAcceptingUserSelector);
  const voiceSessionStatus = useAppSelector(voiceSessionStatusSelector);
  const isEmergencyCall = useAppSelector(isEmergencyCallSelector);
  const isPrimaryHearingReconnect = useAppSelector(
    isPrimaryHearingReconnectSelector
  );
  const isWSConnected = useAppSelector(hearingWSConnectionSelector);

  const { handleEndSignMailSession } = useSignMailHandlers();
  const { handleDeafCancel } = useCallDeafParticipantHandlers();
  const { handleRegularHearingCall } = useCallHearingParticipantHandlers();
  const { cancelHearing } = useHearingCancel();
  const { t } = useTranslation('translation', { keyPrefix: 'callDetails' });

  const isThreeDotsMenuButtonPresent = useIsThreeDotsMenuVisible();

  const isReconnectBlockedByCleanup = useReconnectCleanupPending({
    // Only gate while disconnected. isPrimaryHearingReconnect stays true during
    // connecting and would otherwise hide Cancel during an in-flight reconnect.
    isInReconnectState:
      Boolean(isPrimaryHearingReconnect) &&
      primaryHearing.status === 'disconnected',
    voiceSessionStatus,
  });

  // First H2D hearing connect (inbound receive) — not reconnect.
  // Do not key off callDirection; it is not always 'inbound' for this path.
  const isInitialH2dHearingConnect =
    !isDeafToHearing && !primaryHearing.hasConnected;

  // Keep Cancel as spinner briefly so agents do not cancel immediately.
  // Analytics: warning when Cancel unlocks after delay; info for
  // connecting-UI start → hearing connect / Cancel click durations.
  const { isCancelDelayed: isInboundCancelDelayed, reportCancelClicked } =
    useInboundHearingCancelDelay(
      isInitialH2dHearingConnect &&
        (primaryHearing.status === 'connecting' ||
          isHearingToDeafCallerLoading),
      primaryHearing.status === 'connected'
    );
  const deafToHearingCallerButton = useMemo(() => {
    if (deaf.status === 'connecting' && !isTeamingInProgress) {
      if (isEmergencyCall) {
        return <EmergencyCallDetailsCalleeConnecting />;
      }

      return (
        <CallDetailsButtonCancel
          loading={deaf.isLoading}
          onClick={handleDeafCancel}
        />
      );
    }
    if (deaf.status === 'connected') {
      return (
        <CallDetailsDeafConnectedButtons
          isLoadingDeaf={deaf.isLoading}
          isThreeDotsMenuButtonPresent={isThreeDotsMenuButtonPresent}
        />
      );
    }

    if (deaf.status === 'disconnected') {
      return <CallDetailsButtonDeafReconnect phoneNumber={deaf.phoneNumber} />;
    }

    if (deaf.status === 'unavailable') {
      return (
        <CallDetailsButtonDisconnect
          loading={deaf.isLoading}
          onClick={handleEndSignMailSession}
          buttonType='deaf signmail'
        />
      );
    }
  }, [
    deaf.status,
    deaf.isLoading,
    deaf.phoneNumber,
    isTeamingInProgress,
    isEmergencyCall,
    handleDeafCancel,
    handleEndSignMailSession,
    isThreeDotsMenuButtonPresent,
  ]);

  const hearingToDeafCallerButton = useMemo(() => {
    if (isAcceptingUser) {
      return null;
    }

    if (isReconnectBlockedByCleanup) {
      return (
        <CallDetailsButtonReconnect
          loading
          disabled
          hotkey={Hotkey.CallerConnectionStateChange}
        />
      );
    }

    if (primaryHearing.status === 'connecting') {
      // Initial H2D connect: never wait on WS — failure would spin forever.
      // Reconnect still gates on WS.
      return (
        <CallDetailsButtonCancel
          loading={
            isInboundCancelDelayed ||
            primaryHearing.isLoading ||
            (Boolean(primaryHearing.hasConnected) && !isWSConnected)
          }
          onClick={() => {
            reportCancelClicked();
            cancelHearing({
              id: primaryHearing.id || undefined,
              phoneNumber: primaryHearing.phoneNumber,
            });
          }}
        />
      );
    }

    if (isHearingToDeafCallerLoading) {
      return (
        <CallDetailsButtonCancel
          loading={isInboundCancelDelayed || primaryHearing.isLoading}
          onClick={() => {
            reportCancelClicked();
            cancelHearing({
              id: primaryHearing.id || undefined,
              phoneNumber: primaryHearing.phoneNumber,
            });
          }}
        />
      );
    }

    if (primaryHearing.status === 'connected') {
      return (
        <CallDetailsButtonHearingDisconnect
          hotkey={Hotkey.CallerConnectionStateChange}
        />
      );
    }

    if (isPrimaryHearingReconnect) {
      return (
        <CallDetailsButtonReconnect
          loading={false}
          onClick={() => handleRegularHearingCall(primaryHearing.phoneNumber)}
          hotkey={Hotkey.CallerConnectionStateChange}
        />
      );
    }

    return (
      <CallDetailsButtonCalleeConnect
        isLoading={false}
        onClickHandler={() =>
          handleRegularHearingCall(primaryHearing.phoneNumber)
        }
        hotkey={Hotkey.CallerConnectionStateChange}
      />
    );
  }, [
    primaryHearing.status,
    primaryHearing.id,
    primaryHearing.phoneNumber,
    primaryHearing.isLoading,
    primaryHearing.hasConnected,
    isHearingToDeafCallerLoading,
    isAcceptingUser,
    isWSConnected,
    isInboundCancelDelayed,
    isReconnectBlockedByCleanup,
    handleRegularHearingCall,
    cancelHearing,
    reportCancelClicked,
    isPrimaryHearingReconnect,
  ]);

  const callerButton = useMemo(() => {
    if (isDeafToHearing) {
      return (
        <>
          {deafToHearingCallerButton}
          <CallDetailsParticipantVco2LineContainer />
        </>
      );
    }

    return hearingToDeafCallerButton;
  }, [hearingToDeafCallerButton, deafToHearingCallerButton, isDeafToHearing]);

  const callerMetadata = useMemo(() => {
    if (isDeafToHearing) {
      return deaf;
    }
    return primaryHearing;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    deaf.status,
    deaf.phoneNumber,
    primaryHearing.id,
    primaryHearing.status,
    primaryHearing.phoneNumber,
    primaryHearing.phoneExtension,
    primaryHearing.isWavelloRegistered,
    isDeafToHearing,
  ]);

  const caller = useMemo<CallDetailsParticipantInfo>(() => {
    return {
      ...callerMetadata,
      icon: <CallDetailsParticipantIcon isDeafParticipant={isDeafToHearing} />,
      children: callerButton,
      extensionInput: isDeafToHearing ? undefined : (
        <CallDetailsExtensionInputContainer
          phoneNumber={primaryHearing.phoneNumber}
        />
      ),

      testId: isDeafToHearing ? 'deaf-details-item' : 'hearing-details-item',
      title: t('callFrom'),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    callerMetadata,
    callerButton,
    isDeafToHearing,
    primaryHearing.phoneNumber,
  ]);

  return { caller };
};
